import { Application, Text, Graphics, BlurFilter } from 'pixi.js';
import StageLayers, { StageLayerSet } from '../StageLayers';
import CircleTheme from './themes/circle';
import MainScene from './MainScene';
import { GameClock, setOD } from './timing';
import { mods, speedMultiplier, isModActive } from './mods.js';
import type { ReplayFrame, ReplayBundle } from './lib/utils';
import { TOKENS } from './tokens';
import {
  parseHitCircle,
  parseSlider,
  parseSpinner,
  type HitCircle,
  type Slider,
  type Spinner,
} from '@musicgame/chart';
import { resampleByDistance, Point } from './utils/distanceMap';
import { decodePathCommands, tessellateCommands } from './utils/pathLinker';
import ScoreTracker from './score';
import HealthTracker from './health';
import { decodeBundle } from './utils/decodeBundle';
import {
  coalesceSliderChains,
  assertNoConsecutiveSliders,
  endOf,
} from './utils/coalesceSliderChains';
import { POST_GAP_MS } from './logic/directedChart';

function mulberry32(a: number): () => number {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function renderSplines(
  app: Application,
  notes: (HitCircle | Slider | Spinner)[],
): void {
  const pts: Point[] = [];
  const scale = (p: { x: number; y: number }): Point => ({
    x: (p.x / 9) * app.screen.width,
    y: (p.y / 16) * app.screen.height,
  });

  for (const n of notes) {
    // Sliders may provide an explicit path or list of points. Prefer the
    // encoded ``path`` when available so merged sliders retain their smooth
    // curves when copied or serialized.
    if ('path' in n || 'points' in n) {
      const s = n as Slider;
      let p: { x: number; y: number }[] | undefined;
      if (Array.isArray(s.path)) {
        const cmds = decodePathCommands(s.path);
        p = tessellateCommands(cmds);
      } else if (Array.isArray(s.points)) {
        p = s.points;
      }
      if (p && p.length) {
        for (const pt of p) pts.push(scale(pt));
        continue;
      }
    }
    // Default to the note's position.
    pts.push(scale(n.pos));
  }

  if (pts.length < 2) return;
  const uniform = resampleByDistance(pts, pts.length);
  const g = new Graphics();
  g.moveTo(uniform[0].x, uniform[0].y);
  for (let i = 1; i < uniform.length; i++) {
    g.lineTo(uniform[i].x, uniform[i].y);
  }
  g.stroke({ width: 4, color: 0xffffff, alpha: 0.3 });
  app.stage.addChild(g);
}

/**
 * Simple player for rendering Beatstage charts that supports seeking. This is
 * primarily intended for previewing charts during recording review where
 * deterministic playback is required.
 */
export class BeatstagePlayer {
  private ctx: AudioContext = GameClock.context!;
  public app: Application;
  public theme = new CircleTheme();
  private buffer: AudioBuffer | null = null;
  private notes: (HitCircle | Slider | Spinner)[] = [];
  private showAll = false;
  private src: AudioBufferSourceNode | null = null;
  private startCtx = 0;
  private startOffset = 0;
  private root?: HTMLElement;
  private scene: MainScene | null = null;
  private bundle: any;
  private rngSeed = 0;
  private chartHash: string | null = null;
  private replayData: ReplayFrame[] | null = null;
  private replayIdx = 0;
  public difficulty = { cs: 3, od: 8, hp: 5, sv: 1, ar: 7 };
  private timing: { time: number; bpm: number; meter: number }[] = [];
  private destroyed = false;
  private ownsApp = true;
  private logger?: (msg: string) => void;
  private tracker: ScoreTracker | null = null;
  private layers: StageLayerSet | null = null;
    private videoEl: HTMLVideoElement | null = null;
    private videoUrl: string | null = null;
    private resizeListener: (() => void) | null = null;

  private log(msg: string): void {
    console.log(msg);
    this.logger?.(msg);
  }

  constructor(app?: Application, logger?: (msg: string) => void) {
    this.app = app ?? new Application();
    this.ownsApp = !app;
    this.logger = logger;
  }

  setSeed(seed: number): void {
    this.rngSeed = seed;
  }

  private runReplay = (): void => {
    if (!this.replayData || !this.scene) return;
    const now = GameClock.now() + this.startOffset * 1000;
    while (
      this.replayIdx < this.replayData.length &&
      this.replayData[this.replayIdx].t <= now
    ) {
      const frame = this.replayData[this.replayIdx++];
      this.scene.feedInput(frame);
    }
    if (this.replayIdx >= this.replayData.length) {
      this.app.ticker.remove(this.runReplay);
    }
  };

  getReplay(): ReplayBundle {
    const deviceOffset =
      typeof localStorage !== 'undefined'
        ? Number(localStorage.getItem('audio.offsetMs') ?? '0')
        : 0;
    return {
      metadata: this.bundle?.metadata,
      seed: this.rngSeed,
      events: this.scene?.getRecording() ?? [],
      mods: { ...mods },
      deviceOffset,
      chartHash: this.chartHash,
    };
  }

  serializeReplay(): string {
    return JSON.stringify(this.getReplay());
  }

  /** Briefly shake the background layer. */
  shakeBackground(): void {
    const bg = this.layers?.bg;
    if (!bg) return;
    const origX = bg.position.x;
    const origY = bg.position.y;
    const origFilters = bg.filters ?? [];
    const blur = new BlurFilter(2);
    const frames = [
      { x: origX - 2, y: origY + 2, filters: origFilters },
      { x: origX + 2, y: origY - 2, filters: [...origFilters, blur] },
      { x: origX - 1, y: origY + 1, filters: origFilters },
      { x: origX, y: origY, filters: origFilters },
    ];
    let i = 0;
    const step = () => {
      const frame = frames[i++];
      if (!frame) return;
      bg.position.set(frame.x, frame.y);
      bg.filters = frame.filters;
      if (i < frames.length) setTimeout(step, 25);
    };
    step();
  }

  /** Create and initialize a player instance. */
  static async create(
    encoded: string,
    audio: Blob | string,
    root: HTMLElement,
    showAll = false,
    app?: Application,
    logger?: (msg: string) => void,
    video?: Blob | string,
  ): Promise<BeatstagePlayer> {
    const player = new BeatstagePlayer(app, logger);
    player.showAll = showAll;
    await player.init(encoded, audio, root, app, video);
    return player;
  }

  private async init(
    encoded: string,
    audio: Blob | string,
    root: HTMLElement,
    existingApp?: Application,
    video?: Blob | string,
  ) {
    this.root = root;
    const bundle = await decodeBundle(encoded);
    this.bundle = bundle;
    let videoSource: Blob | string | null = video ?? null;
    if (!videoSource && bundle.video?.data) {
      try {
        const bytes = Uint8Array.from(atob(bundle.video.data), (c) => c.charCodeAt(0));
        const mime = (bundle.meta?.video?.mime as string) || 'video/webp';
        videoSource = new Blob([bytes], { type: mime });
      } catch (e) {
        console.warn('[BeatstagePlayer] failed to decode video', e);
      }
    }
    if (videoSource) {
      this.videoUrl =
        typeof videoSource === 'string' ? videoSource : URL.createObjectURL(videoSource);
      this.videoEl = document.createElement('video');
      this.videoEl.loop = true;
      this.videoEl.muted = true;
      this.videoEl.src = this.videoUrl;
      this.videoEl.playsInline = true;
    }
    // Handle both legacy array-based timing and new segment-based timing
    this.timing = Array.isArray(bundle.timing)
      ? bundle.timing
      : bundle.timing?.segments?.map((s: any) => ({
          time: s.tStartMs,
          bpm: s.bpm,
          meter: 4,
        })) ?? [];
    if (typeof crypto !== 'undefined') {
      const digest = await crypto.subtle.digest(
        'SHA-256',
        new TextEncoder().encode(encoded),
      );
      const hashArray = Array.from(new Uint8Array(digest));
      this.chartHash = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    }
    this.difficulty = { cs: 3, od: 8, hp: 5, sv: 1, ar: 7 };
    this.theme.setDifficulty({ cs: this.difficulty.cs, sv: this.difficulty.sv });
    setOD(this.difficulty.od);
    const audioInfo = { file: '', offset: bundle.audio?.offset ?? 0 };
    this.notes = (bundle.hitObjects ?? []).map((o: any) => {
      const ar = o.approachRate ?? this.difficulty.ar;

      // Normalize timing fields. Some generators (e.g. directedChart) use
      // ``tMs``/``durMs`` while others provide ``tStart``/``tEnd`` or
      // second-based ``time``/``endTime`` fields. Derive ``tStart`` and
      // ``tEnd`` in milliseconds for downstream utilities like
      // ``coalesceSliderChains`` which rely on explicit start/end times.
      const baseStart =
        o.tStart ?? o.tMs ?? ((o.time ?? 0) * 1000);
      const baseEnd =
        o.tEnd ??
        (o.durMs != null ? baseStart + o.durMs : ((o.endTime ?? 0) * 1000));

      if (o.type === 'circle') {
        return parseHitCircle(
          {
            time: baseStart / 1000,
            x: o.x ?? o.pos?.x ?? 0,
            y: o.y ?? o.pos?.y ?? 0,
          },
          ar,
          audioInfo,
        );
      }
      if (o.type === 'slider') {
        const bpmChanges = extractBpmChanges(this.timing, baseStart, baseEnd);
        const inputPts = Array.isArray(o.path) ? undefined : o.points ?? o.pts;
        const parsed = parseSlider(
          {
            time: baseStart / 1000,
            endTime: baseEnd / 1000,
            x: o.x ?? o.pos?.x ?? 0,
            y: o.y ?? o.pos?.y ?? 0,
            path: o.path,
            points: inputPts,
          },
          ar,
          audioInfo,
        );
        const note: any = {
          ...parsed,
          points: parsed.points ?? inputPts,
          // Ensure merged sliders retain their encoded path so that downstream
          // renderers can reconstruct smooth curves rather than straight line
          // segments when notes are copied or serialized.
          path: (parsed.path ?? o.path)?.slice(),
          repeats: o.repeats,
          pixelLength: o.pixelLength ?? parsed.pixelLength,
          sv: o.sv,
          bpmChanges,
          beatDivisor: o.beatDivisor ?? 1,
        };
        note.duration = note.tEnd - note.tStart;
        return note;
      }
      if (o.type === 'spinner') {
        return parseSpinner(
          {
            time: baseStart / 1000,
            endTime: baseEnd / 1000,
            x: o.x ?? o.pos?.x ?? 0,
            y: o.y ?? o.pos?.y ?? 0,
          },
          ar,
          audioInfo,
        );
      }
      return o;
    });

    this.notes = coalesceSliderChains(this.notes, POST_GAP_MS, 10);

    const sliderNotes = this.notes.filter((n) => n.type === 'slider');
    for (let i = 1; i < sliderNotes.length; i++) {
      const prev: any = sliderNotes[i - 1];
      const curr: any = sliderNotes[i];
      const prevEnd = endOf(prev);
      const currStart = curr.tStart;
      const gap = currStart - prevEnd;
      if (
        Number.isFinite(prevEnd) &&
        Number.isFinite(currStart) &&
        currStart <= prevEnd
      ) {
        const prevIdx = this.notes.indexOf(prev);
        const currIdx = this.notes.indexOf(curr);
        this.log(
          `[BeatstagePlayer] consecutive slider detected: #${prevIdx} end=${prevEnd}ms -> #${currIdx} start=${currStart}ms (gap ${gap}ms)`,
        );
      } else if (!Number.isFinite(prevEnd) || !Number.isFinite(currStart)) {
        const prevIdx = this.notes.indexOf(prev);
        const currIdx = this.notes.indexOf(curr);
        this.log(
          `[BeatstagePlayer] slider timing invalid for #${prevIdx} or #${currIdx} (end=${prevEnd}, start=${currStart})`,
        );
      }
    }

    assertNoConsecutiveSliders(this.notes);

    if (this.ctx.state === 'suspended') await this.ctx.resume();

    // Load audio buffer from either a blob or URL
    let arrayBuffer: ArrayBuffer;
    if (typeof audio === 'string') {
      const res = await fetch(audio);
      arrayBuffer = await res.arrayBuffer();
    } else {
      arrayBuffer = await audio.arrayBuffer();
    }
    console.log('[BeatstagePlayer] arrayBuffer length', arrayBuffer.byteLength);
    try {
      console.log('[BeatstagePlayer] decodeAudioData start');
      this.buffer = await this.ctx.decodeAudioData(arrayBuffer.slice(0));
      console.log('[BeatstagePlayer] decodeAudioData end');
    } catch (err) {
      console.error('[BeatstagePlayer] decodeAudioData error', err);
      throw err;
    }
    if (this.buffer) {
      console.log('[BeatstagePlayer] buffer.sampleRate', this.buffer.sampleRate);
      console.log('[BeatstagePlayer] buffer.duration', this.buffer.duration);
      console.log('[BeatstagePlayer] buffer.numberOfChannels', this.buffer.numberOfChannels);
    }

    // Init Pixi app and theme
      if (!existingApp) {
        const width = TOKENS.stage.w;
        const height = TOKENS.stage.h;
        await this.app.init({ width, height, backgroundAlpha: 0 });
        root.appendChild(this.app.canvas);
        root.style.position = 'relative';
        this.resizeListener = () => {
          const h = window.innerHeight;
          const w = (h * TOKENS.stage.w) / TOKENS.stage.h;
          root.style.width = `${w}px`;
          root.style.height = `${h}px`;
        };
        this.resizeListener();
        window.addEventListener('resize', this.resizeListener);
        this.app.canvas.style.width = '100%';
        this.app.canvas.style.height = '100%';
      }
    this.layers = StageLayers(this.app, { beatPulse: true });
    if (this.videoEl) {
      this.layers.setBackgroundVideo(this.videoEl);
      this.layers.setScanlines(true);
    }
    this.app.renderer.on('resize', () => {
      this.layers?.resize(this.app.screen.width, this.app.screen.height);
    });
    this.theme.init(this.app);
  }

  /** Begin playback from a specific time in seconds.
   * @param time - Offset into the song in seconds.
   * @param replay - Optional replay events.
   * @param startAt - Optional absolute AudioContext time to begin playback.
   */
  async playFrom(time = 0, replay?: ReplayFrame[], startAt?: number): Promise<void> {
    this.log('[BeatstagePlayer] playFrom called');
    if (!this.buffer) {
      console.warn('[BeatstagePlayer] No audio buffer loaded');
      return;
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    this.stop();
    if (this.layers) {
      this.layers.paths.removeChildren();
      this.layers.notes.removeChildren();
      this.layers.fx.removeChildren();
      this.layers.hud.removeChildren();
    } else if (this.ownsApp) {
      this.layers = StageLayers(this.app, { beatPulse: true });
    }
    if (this.videoEl) {
      this.layers?.setBackgroundVideo(this.videoEl);
      this.layers?.setScanlines(true);
    }
    this.theme.init(this.app);
    this.theme.setDifficulty({ cs: this.difficulty.cs, sv: this.difficulty.sv });

    const src = this.ctx.createBufferSource();
    src.buffer = this.buffer;
    src.connect(this.ctx.destination);
    src.playbackRate.value = speedMultiplier();
    this.log('[BeatstagePlayer] Preparing to start audio source');
    src.onended = () => this.log('[BeatstagePlayer] Audio source ended');
    (src as any).onerror = (e: any) =>
      console.error('[BeatstagePlayer] Audio source error', e);
    try {
      if (startAt !== undefined) src.start(startAt, time);
      else src.start(0, time);
      this.log('[BeatstagePlayer] Audio source started');
    } catch (e) {
      console.error('[BeatstagePlayer] Failed to start audio source', e);
      throw e;
    }
    this.src = src;

    this.startCtx = startAt ?? this.ctx.currentTime;
    this.startOffset = time;
    GameClock.start(this.startCtx);
    this.log('[BeatstagePlayer] Clock started');
    const startMs = time * 1000;
    this.replayData = replay ?? null;
    if (this.replayData) {
      this.replayIdx = this.replayData.findIndex((f) => f.t >= startMs);
      if (this.replayIdx === -1) this.replayIdx = this.replayData.length;
    } else {
      this.replayIdx = 0;
    }
    this.scene = new MainScene(
      this.app,
      this.layers!,
      this.theme,
      1,
      -time,
      !!this.replayData,
      { cs: this.difficulty.cs },
      this.logger,
    );
    const futureNotes = this.notes.filter((n) => n.tStart >= startMs);
    this.scene.loadNotes(futureNotes);
    this.log('[BeatstagePlayer] Notes loaded');

    if (!this.tracker) {
      const health = isModActive('easy')
        ? undefined
        : new HealthTracker(1, this.difficulty.hp);
      this.tracker = new ScoreTracker(health);
      this.app.stage.on('hit', (result: string, info?: unknown) => {
        this.tracker!.register(result);
        if (result === 'miss' && info) {
          console.log('[BeatstagePlayer] miss details', info);
        }
        this.log(
          `score ${result} -> ${this.tracker!.score} (combo ${this.tracker!.combo})`,
        );
      });
      this.app.stage.on('spinner', (progress: number) => {
        this.tracker!.register('spinner', progress);
        this.log(
          `spinner ${progress.toFixed(2)} -> ${this.tracker!.score} (combo ${this.tracker!.combo})`,
        );
      });
    }
    if (this.replayData) {
      this.app.ticker.add(this.runReplay);
      this.log('[BeatstagePlayer] Replay playback scheduled');
    }
    // Debug logging of current playback time has been removed to avoid noisy
    // console output during development.
    if (this.showAll) {
      renderSplines(this.app, this.notes);
      this.log('[BeatstagePlayer] Splines rendered');
    }
    if (this.videoEl) {
      try {
        const dur = this.videoEl.duration || 1;
        this.videoEl.currentTime = time % dur;
        if (startAt !== undefined) {
          this.videoEl.pause();
          const delay = (startAt - this.ctx.currentTime) * 1000;
          setTimeout(() => void this.videoEl?.play(), Math.max(0, delay));
        } else {
          void this.videoEl.play();
        }
      } catch (e) {
        console.warn('[BeatstagePlayer] video play failed', e);
      }
    }
  }

  /** Stop current playback. */
  stop(): void {
    this.log('[BeatstagePlayer] Stopping playback');
    GameClock.clear();
    try {
      this.src?.stop();
    } catch (e) {
      console.error('[BeatstagePlayer] Error stopping audio source', e);
    }
    this.src?.disconnect();
    this.src = null;
    this.scene?.destroy();
    this.scene = null;
    this.app.ticker?.remove(this.runReplay);
    this.replayData = null;
    if (this.videoEl) {
      this.videoEl.pause();
    }
  }

  /** Destroy the player and associated resources. */
  destroy(): void {
    if (this.destroyed) return;
    this.stop();
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
      this.resizeListener = null;
    }
    if (this.videoEl) {
      this.layers?.setBackgroundVideo(null);
      this.videoEl.pause();
      if (this.videoUrl && this.videoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.videoUrl);
      }
      this.videoEl.src = '';
      this.videoEl = null;
      this.videoUrl = null;
    }
    if (this.ownsApp) {
      this.app.destroy(true);
    }
    this.destroyed = true;
  }

  /** Get current playback time in seconds. */
  get currentTime(): number {
    const elapsed = (this.ctx.currentTime - this.startCtx) * speedMultiplier();
    return this.startOffset + elapsed;
  }

  /** Total length of the audio buffer. */
  get duration(): number {
    return this.buffer?.duration ?? 0;
  }
}

function extractBpmChanges(
  timing: { time: number; bpm: number; meter: number }[],
  start: number,
  end: number,
): { time: number; bpm: number; meter: number }[] {
  const arr: { time: number; bpm: number; meter: number }[] = [];
  let current = timing[0] ?? { time: 0, bpm: 120, meter: 4 };
  for (const tp of timing) {
    if (tp.time <= start) current = tp;
    else if (tp.time < end) arr.push(tp);
  }
  return [current, ...arr];
}

/**
 * Legacy helper to quickly play a chart. Returns the underlying player so
 * callers may control playback (e.g. seek) if desired.
 */
export async function playBeatstage(
  encoded: string,
  audio: Blob | string,
  root: HTMLElement,
  showAll = false,
  replay?: ReplayBundle,
  video?: Blob | string,
  startAt?: number,
  onFail?: () => void,
): Promise<BeatstagePlayer> {
  const seed = replay?.seed ?? (Math.random() * 0xffffffff) >>> 0;
  const rng = mulberry32(seed);
  Math.random = rng;
  if (replay?.mods) Object.assign(mods, replay.mods);

  const player = await BeatstagePlayer.create(
    encoded,
    audio,
    root,
    showAll,
    undefined,
    undefined,
    video,
  );
  player.setSeed(seed);
  await player.playFrom(0, replay?.events, startAt);

  // Attach simple score tracking just like the original helper
  const health = isModActive('easy')
    ? undefined
    : new HealthTracker(1, player.difficulty.hp);
  const tracker = new ScoreTracker(health);
  const scoreText = new Text({ text: 'Score: 0', style: { fill: 0xffffff, fontSize: 32 } });
  scoreText.x = 10;
  scoreText.y = 10;
  player.app.stage.addChild(scoreText);

  const comboText = new Text({ text: 'Combo: 0', style: { fill: 0xffffff, fontSize: 32 } });
  comboText.x = 10;
  comboText.y = 50;
  player.app.stage.addChild(comboText);

  const accText = new Text({ text: 'Acc: 0.00%', style: { fill: 0xffffff, fontSize: 32 } });
  accText.x = 10;
  accText.y = 90;
  player.app.stage.addChild(accText);

  const gradeText = new Text({ text: 'Grade: -', style: { fill: 0xffffff, fontSize: 32 } });
  gradeText.x = 10;
  gradeText.y = 130;
  player.app.stage.addChild(gradeText);

  let hpBg: Graphics | undefined;
  let hpBar: Graphics | undefined;
  const drawHp = () => {
    if (!health || !hpBar) return;
    hpBar.clear();
    hpBar.rect(10, 170, 200 * health.hp, 20).fill({ color: 0x00ff00 });
  };
  if (health) {
    hpBg = new Graphics();
    hpBg.rect(10, 170, 200, 20).fill({ color: 0x333333 });
    player.app.stage.addChild(hpBg);
    hpBar = new Graphics();
    player.app.stage.addChild(hpBar);
    drawHp();
  }

  const feedbackText = new Text({ text: '', style: { fill: 0xffffff, fontSize: 48 } });
  feedbackText.anchor.set(0.5);
  feedbackText.x = player.app.screen.width / 2;
  feedbackText.y = player.app.screen.height / 2;
  player.app.stage.addChild(feedbackText);
  const timingText = new Text({ text: '', style: { fill: 0xffffff, fontSize: 32 } });
  timingText.anchor.set(0.5);
  timingText.x = feedbackText.x;
  timingText.y = feedbackText.y + 40;
  player.app.stage.addChild(timingText);

  player.theme.registerInputs(player.app);

  const colors = {
    perfect: 0x00ffff,
    great: 0x00ff00,
    good: 0xffff00,
    miss: 0xff0000,
    spinner: 0xff00ff,
  } as const;
  player.app.stage.on('hit', (result: string, info?: unknown) => {
    tracker.register(result);
    if (result === 'miss' && info) {
      console.log('[Play] miss details', info);
    }
    if (result in colors) {
      feedbackText.text = result.toUpperCase();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      feedbackText.style.fill = (colors as any)[result];
      if (result !== 'perfect') {
        let timing = '';
        if (info && typeof info === 'object' && 'diff' in info) {
          const d = (info as any).diff as number;
          if (d < 0) timing = 'EARLY';
          else if (d > 0) timing = 'LATE';
        }
        timingText.text = timing;
      } else {
        timingText.text = '';
      }
      setTimeout(() => {
        feedbackText.text = '';
        timingText.text = '';
      }, 300);
    }
    scoreText.text = `Score: ${tracker.score}`;
    comboText.text = `Combo: ${tracker.combo}`;
    accText.text = `Acc: ${tracker.accuracy.toFixed(2)}%`;
    gradeText.text = `Grade: ${tracker.grade}`;
  });

  player.app.stage.on('spinner', (progress: number) => {
    tracker.register('spinner', progress);
    timingText.text = '';
    if (progress >= 1) {
      feedbackText.text = 'SPIN!';
      feedbackText.style.fill = colors.spinner;
    } else {
      feedbackText.text = 'MISS';
      feedbackText.style.fill = colors.miss;
    }
    setTimeout(() => {
      feedbackText.text = '';
      timingText.text = '';
    }, 300);
    scoreText.text = `Score: ${tracker.score}`;
    comboText.text = `Combo: ${tracker.combo}`;
    accText.text = `Acc: ${tracker.accuracy.toFixed(2)}%`;
    gradeText.text = `Grade: ${tracker.grade}`;
    drawHp();
  });

  let failed = false;
  if (health) {
    player.app.ticker.add(() => {
      // Passive health drain is disabled during play.
      drawHp();
      if (!failed && !mods.noFail && health.hp <= 0) {
        failed = true;
        feedbackText.text = 'FAILED';
        timingText.text = '';
        player.stop();
        onFail?.();
      }
    });
  }

  return player;
}
